import { create } from 'zustand';
import {
  getAllCrmCategories,
  getCrmCategories,
  getCustomerData,
  getCxComplaintTicket,
  getTicketConfig,
  getTicketConfigByOrderId,
  getTicketSlot,
  getHelpAndSupportCategories,
  postTicket,
  postProductChange,
  postOrdersToReturn,
  updateTicketsCaseIds
} from '../api';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { persist } from 'zustand/middleware';

export const useOrderSearchForTicket = create((set, get) => ({
  customerDetails: [],
  crmCategories: [],
  allCrmCategories: [],
  helpAndSupportCategories: [],
  isLoading: false,
  isError: false,
  fetchCustomerDetails: async (key) => {
    try {
      const response = await getCustomerData(key);
      set({ customerDetails: response.data, isLoading: false });
    } catch (error) {
      const errorMessage = _.get(error, ['response', 'data', 'message'], 'Failed to fetch customer details.');
      const nestedErrorMessage = _.get(error, ['response', 'data', 'message', 'message'], errorMessage);
      set({ isError: true, isLoading: false });
      toast.error(nestedErrorMessage);
    }
  },
  fetchCrmCategories: async () => {
    try {
      const response = await getCrmCategories();
      set({ crmCategories: response.data.data, isLoading: false });
    } catch (error) {
      const errorMessage = _.get(error, ['response', 'data', 'message'], 'Failed to fetch CRM categories.');
      const nestedErrorMessage = _.get(error, ['response', 'data', 'message', 'message'], errorMessage);
      set({ isError: true, isLoading: false });
      toast.error(nestedErrorMessage);
    }
  },
  fetchAllCrmCategories: async () => {
    try {
      const response = await getAllCrmCategories();
      set({ allCrmCategories: response.data.data, isLoading: false });
    } catch (error) {
      const errorMessage = _.get(error, ['response', 'data', 'message'], 'Failed to fetch all CRM categories.');
      const nestedErrorMessage = _.get(error, ['response', 'data', 'message', 'message'], errorMessage);
      set({ isError: true, isLoading: false });
      toast.error(nestedErrorMessage);
    }
  },
  fetchHelpAndSupportCategories: async () => {
    try {
      const response = await getHelpAndSupportCategories();
      set({ helpAndSupportCategories: response.data.data, isLoading: false });
    } catch (error) {
      const errorMessage = _.get(error, ['response', 'data', 'message'], 'Failed to fetch Help & Support categories.');
      const nestedErrorMessage = _.get(error, ['response', 'data', 'message', 'message'], errorMessage);
      set({ isError: true, isLoading: false });
      toast.error(nestedErrorMessage);
    }
  },
}));

export const useCreateTicket = create(set => ({
  ticketResponse: {},
  ticketLoading: false,
  createTicket: async (payload, key) => {
    try {
      set({ ticketLoading: true });
      const response = await postTicket(payload);
      set({ ticketResponse: response.data });
      toast.success('Ticket Created');
      return { success: true, data: response.data };
    } catch (error) {
      const statusCode = _.get(error, ['response', 'status']);
      const fallbackError = _.get(error, ['message'], 'Failed to Create Ticket.');
      const errorMessage = _.get(error, ['response', 'data', 'message'], fallbackError);
      const nestedErrorMessage = _.get(error, ['response', 'data', 'message', 'message'], errorMessage);

      if (statusCode === 409) {
        toast.warn(errorMessage.replaceAll('_', ' '));
      } else if (statusCode !== 422) {
        toast.error(nestedErrorMessage);
      }
      return { success: false, status: statusCode, message: nestedErrorMessage };
    } finally {
      set({ ticketLoading: false });
    }
  },

  placeReturnRequest: async (payload) => {
    try {
      set({ ticketLoading: true });
      // No toast.success here — a 201 only means iThink answered, not that
      // it accepted the shipment (a rejection comes back as message inside
      // a 2xx). The caller re-checks the actual ReturnRequest record for a
      // waybill before telling the agent it succeeded.
      const response = await postOrdersToReturn(payload);
      return { success: true, data: response.data };
    } catch (error) {
      const statusCode = _.get(error, ['response', 'status']);
      const fallbackError = _.get(error, ['message'], 'Failed to place the reverse pickup.');
      const errorMessage = _.get(error, ['response', 'data', 'message'], fallbackError);
      toast.error(errorMessage);
      return { success: false, status: statusCode, message: errorMessage };
    } finally {
      set({ ticketLoading: false });
    }
  },

  submitProductChange: async payload => {
    try {
      set({ ticketLoading: true });
      const response = await postProductChange(payload);
      // Name what was actually created: a COD order comes back with its display id, a prepaid one
      // with the payment link reference. Saying only 'created' leaves the agent nothing to quote.
      const replacement = _.get(response, ['data', 'replacement_order'], {});
      const orderDisplayId = _.get(replacement, 'order_display_id', null);
      const linkRef = _.get(replacement, 'link_ref', null);
      toast.success(orderDisplayId
        ? `Replacement order ${orderDisplayId} created`
        : linkRef
          ? `Prepaid payment link created (ref ${linkRef})`
          : 'Replacement order created');
      if (_.get(response, ['data', 'ticket_closed']) === false) {
        toast.warn('The replacement was created but the product change ticket could not be closed');
      }
      return { success: true, data: response.data };
    } catch (error) {
      const fallbackError = _.get(error, ['message'], 'Failed to process the product change.');
      const errorMessage = _.get(error, ['response', 'data', 'message'], fallbackError);
      toast.error(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      set({ ticketLoading: false });
    }
  },
}));

export const useTicketConfig = create((set, get) => ({
  ticketConfig: {},
  ticketConfigByOrderId: {},

  fetchTicketConfig: async caseId => {
    if (!caseId) return null;
    try {
      const response = await getTicketConfig(caseId);
      set({ ticketConfig: response.data });
      return response.data; // ✅ always return
    } catch (error) {
      const errorMessage = _.get(error, ['response', 'data', 'message'], 'Something went wrong');
      toast.error(errorMessage);
      return null;
    }
  },

  fetchTicketConfigByOrderId: async (orderId, category) => {
    try {
      const response = await getTicketConfigByOrderId(orderId, category);
      const data = _.get(response, ['data', orderId ], {});
      set({ ticketConfigByOrderId: data });
    } catch (error) {
      const errorMessage = _.get(error, ['response', 'data', 'message'], 'Something went wrong');
      toast.error(errorMessage);
      return null;
    }
  },
}));

export const useTicketSlots = create(set => ({
	ticketSlots: {},
  ticketSlotLoading: false,
  fetchTicketSlotData: async (category, language) => {
    try {
      set({ allSlotLoading: true, error: null });
      const response = await getTicketSlot(category, language);
      set({ ticketSlots: response.data, ticketSlotLoading: false });
    } catch (error) {
      toast.error(error?.response?.data?.message);
      set({ error: error.message, ticketSlotLoading: false });
    }
  }
}));

export const useCxComplaintTicket = create(set => ({
	ticketData: [],
  ticketLoading: false,
  error: false,
  rowCount: 0,
  fetchCxComplaintTicket: async (sd, ed, newPage, query) => {
    try {
      set({ ticketLoading: true, error: null });
      const response = await getCxComplaintTicket(sd, ed, newPage, query);
      const { row_count, tickets } = response.data;
      set({ ticketData: tickets, rowCount: row_count, ticketLoading: false });
    } catch (error) {
      toast.error(error?.response?.data?.message);
      set({ error: error.message, ticketLoading: false });
    }
  }
}));

export const useMapMissingTicket = create(set => ({
  updatedTickets: null,
  updateTicketsLoading: false,
  updateTicketsError: null,

  updateTicketsCaseIds: async (ticketIds) => {
    try {
      set({ updateTicketsLoading: true, updateTicketsError: null });
      const response = await updateTicketsCaseIds(ticketIds);

      if (response.data.success) {
        set({
          updatedTickets: response.data.updatedTickets,
          updateTicketsLoading: false
        });
        toast.success(response.data.message);
      } else {
        set({
          updateTicketsError: response.data.message,
          updateTicketsLoading: false,
          updatedTickets: []
        });
        toast.error(response.data.message);
      }
    } catch (error) {
      const errorMessage = _.get(error, ['response', 'data', 'message'], 'Failed to update tickets case IDs');
      set({
        updateTicketsError: errorMessage,
        updateTicketsLoading: false,
        updatedTickets: []
      });
      toast.error(errorMessage);
    }
  },

  clearResults: () => {
    set({
      updatedTickets: null,
      updateTicketsError: null
    });
  }
}));